import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Navigation,
  Pagination,
  Autoplay,
  EffectCoverflow,
} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";
import {
  FaArrowRight,
  FaExternalLinkAlt,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaProjectDiagram,
  FaLaptopCode,
  FaGlobeAmericas,
  FaCogs,
} from "react-icons/fa";
import portfolioData from "../portfolio/portfolio.json";
import { getProjectImage } from "../portfolio/images";

// Color palette mapped by category for consistency
const categoryColors = {
  "Government & Civic": "from-blue-600 to-cyan-500",
  "Business & Commerce": "from-purple-600 to-indigo-500",
  "SaaS & Tools": "from-orange-500 to-amber-500",
  "Nonprofit & Community": "from-teal-500 to-green-500",
  "Insurance & Finance": "from-rose-500 to-pink-500",
  "Travel & Logistics": "from-sky-500 to-blue-500",
  Healthcare: "from-emerald-500 to-teal-500",
};

const getColor = (category) =>
  categoryColors[category] || "from-primary to-teal";

// Animated counter hook
const useCounter = (end, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!shouldStart) return;
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration, shouldStart]);
  return count;
};

// Stats bar component
const StatsBar = memo(({ isVisible }) => {
  const projectCount = useCounter(
    portfolioData.projects.length,
    1500,
    isVisible,
  );
  const categoryCount = useCounter(
    new Set(portfolioData.projects.map((p) => p.category)).size,
    1500,
    isVisible,
  );

  const stats = [
    {
      icon: <FaProjectDiagram />,
      value: projectCount,
      label: "Projects Delivered",
      suffix: "+",
    },
    {
      icon: <FaGlobeAmericas />,
      value: categoryCount,
      label: "Industries Served",
      suffix: "",
    },
    {
      icon: <FaLaptopCode />,
      value: useCounter(100, 1800, isVisible),
      label: "Client Satisfaction",
      suffix: "%",
    },
    {
      icon: <FaCogs />,
      value: useCounter(24, 1600, isVisible),
      label: "Support & Maintenance",
      suffix: "/7",
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16 md:mb-20">
      {stats.map((stat, index) => (
        <div
          key={index}
          className="group relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-5 md:p-6 rounded-2xl border border-gray-200/50 dark:border-gray-700/50 text-center hover:border-primary/30 dark:hover:border-teal/30 transition-all duration-500 hover:shadow-lg"
        >
          <div className="text-primary dark:text-teal mb-2 flex justify-center text-xl group-hover:scale-110 transition-transform duration-300">
            {stat.icon}
          </div>
          <div className="text-2xl md:text-3xl font-bold gradient-text mb-1">
            {stat.value}
            {stat.suffix}
          </div>
          <div className="text-xs md:text-sm text-gray-500 dark:text-gray-400 font-medium">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );
});

// Lightbox Modal component
const ProjectModal = memo(
  ({ project, onClose, onPrev, onNext, hasPrev, hasNext }) => {
    const imageSrc = getProjectImage(project.image);
    const color = getColor(project.category);

    // Keyboard navigation
    useEffect(() => {
      const handleKey = (e) => {
        if (e.key === "Escape") onClose();
        if (e.key === "ArrowLeft" && hasPrev) onPrev();
        if (e.key === "ArrowRight" && hasNext) onNext();
      };
      document.addEventListener("keydown", handleKey);
      document.body.style.overflow = "hidden";
      return () => {
        document.removeEventListener("keydown", handleKey);
        document.body.style.overflow = "";
      };
    }, [onClose, onPrev, onNext, hasPrev, hasNext]);

    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8"
        role="dialog"
        aria-modal="true"
        aria-label={project.title}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/70 backdrop-blur-sm portfolio-modal-enter"
          onClick={onClose}
        />

        {/* Modal Content */}
        <div className="relative w-full max-w-5xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden portfolio-modal-content-enter flex flex-col">
          {/* Gradient top bar */}
          <div
            className={`h-1.5 bg-gradient-to-r ${color} flex-shrink-0`}
          ></div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 hover:rotate-90 group"
            aria-label="Close modal"
          >
            <FaTimes className="text-gray-600 dark:text-gray-300 group-hover:text-red-500 transition-colors duration-300" />
          </button>

          {/* Navigation arrows */}
          {hasPrev && (
            <button
              onClick={onPrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 hover:-translate-x-0.5 group"
              aria-label="Previous project"
            >
              <FaChevronLeft className="text-gray-600 dark:text-gray-300 group-hover:text-primary dark:group-hover:text-teal transition-colors duration-300" />
            </button>
          )}
          {hasNext && (
            <button
              onClick={onNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 hover:translate-x-0.5 group"
              aria-label="Next project"
            >
              <FaChevronRight className="text-gray-600 dark:text-gray-300 group-hover:text-primary dark:group-hover:text-teal transition-colors duration-300" />
            </button>
          )}

          <div className="overflow-y-auto flex-1">
            {/* Image Section */}
            {imageSrc && (
              <div className="relative w-full bg-gray-100 dark:bg-gray-800">
                <img
                  src={imageSrc}
                  alt={project.title}
                  className="w-full max-h-[45vh] object-cover object-top"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-900 via-transparent to-transparent"></div>
              </div>
            )}

            {/* Content */}
            <div className="p-6 md:p-8 -mt-8 relative xxxxz-10xxxx-dont-add-this-classes">
              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span
                  className={`inline-flex px-3 py-1 text-xs font-bold rounded-full bg-gradient-to-r ${color} text-white shadow-sm`}
                >
                  {project.category}
                </span>
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                  {project.type}
                </span>
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                  {project.year}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                {project.title}
              </h3>

              {/* Description */}
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-base md:text-lg mb-6">
                {project.description}
              </p>

              {/* Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Tech Stack */}
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                  <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Tech Stack
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(project.techStack || []).map((tech) => (
                      <span
                        key={tech}
                        className="px-2.5 py-1 text-xs font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md border border-gray-200 dark:border-gray-600 shadow-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Role */}
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                  <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Our Role
                  </h4>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {project.role || "Full-Stack Development"}
                  </p>
                </div>
              </div>

              {/* Action Button */}
              {project.url && project.url !== "#" ? (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-teal hover:from-primary-600 hover:to-teal-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Visit Live Site
                  <FaExternalLinkAlt className="text-sm group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                </a>
              ) : (
                <span className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-semibold rounded-xl border border-gray-200 dark:border-gray-700">
                  Private Project
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  },
);

const OurWork = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [modalProject, setModalProject] = useState(null);
  const featuredProjects = portfolioData.projects.slice(0, 6);

  // Intersection observer for stats animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const openModal = useCallback((project) => setModalProject(project), []);
  const closeModal = useCallback(() => setModalProject(null), []);

  const navigateModal = useCallback(
    (direction) => {
      if (!modalProject) return;
      const idx = featuredProjects.findIndex((p) => p.id === modalProject.id);
      const next = idx + direction;
      if (next >= 0 && next < featuredProjects.length) {
        setModalProject(featuredProjects[next]);
      }
    },
    [modalProject, featuredProjects],
  );

  const modalIndex = modalProject
    ? featuredProjects.findIndex((p) => p.id === modalProject.id)
    : -1;

  return (
    <section
      id="our-work"
      ref={sectionRef}
      className="relative py-20 md:py-28 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 overflow-hidden"
    >
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -right-40 w-[500px] h-[500px] bg-primary/5 dark:bg-teal/5 rounded-full filter blur-3xl animate-pulse-slow"></div>
        <div
          className="absolute bottom-1/4 -left-40 w-[400px] h-[400px] bg-teal/5 dark:bg-primary/5 rounded-full filter blur-3xl animate-pulse-slow"
          style={{ animationDelay: "1s" }}
        ></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-accent/3 dark:bg-accent/5 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 md:mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 dark:bg-teal/10 rounded-full mb-6">
            <FaLaptopCode className="text-primary dark:text-teal" />
            <span className="text-sm font-semibold text-primary dark:text-teal">
              Portfolio
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-gray-900 dark:text-white">Our </span>
            <span className="gradient-text">Work</span>
          </h2>
          <div className="h-1 w-24 mx-auto bg-gradient-to-r from-primary via-teal to-accent rounded-full mb-6"></div>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Real solutions we've built for real businesses. Each project
            reflects our commitment to quality, performance, and measurable
            outcomes.
          </p>
        </div>

        {/* Animated Stats */}
        <StatsBar isVisible={isVisible} />

        {/* Featured Projects Carousel */}
        <div className="mb-12">
          {/* Desktop: Coverflow Swiper */}
          <div className="hidden md:block">
            <Swiper
              modules={[Navigation, Pagination, Autoplay, EffectCoverflow]}
              effect="coverflow"
              grabCursor
              centeredSlides
              slidesPerView={2.2}
              spaceBetween={24}
              coverflowEffect={{
                rotate: 0,
                stretch: 0,
                depth: 100,
                modifier: 1.5,
                slideShadows: false,
              }}
              navigation={{
                nextEl: ".ourwork-swiper-next",
                prevEl: ".ourwork-swiper-prev",
              }}
              pagination={{ clickable: true, dynamicBullets: true }}
              autoplay={{
                delay: 5000,
                disableOnInteraction: true,
                pauseOnMouseEnter: true,
              }}
              breakpoints={{
                768: { slidesPerView: 2.2 },
                1024: { slidesPerView: 2.5 },
                1280: { slidesPerView: 3 },
              }}
              className="portfolio-swiper pb-14"
            >
              {featuredProjects.map((project) => {
                const imageSrc = getProjectImage(project.image);
                const color = getColor(project.category);

                return (
                  <SwiperSlide key={project.id}>
                    <button
                      onClick={() => openModal(project)}
                      className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 dark:border-gray-700 w-full text-left focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-teal focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                      aria-label={`View ${project.title} details`}
                    >
                      {/* Screenshot with overlay */}
                      {imageSrc && (
                        <div className="relative overflow-hidden aspect-[16/10]">
                          <img
                            src={imageSrc}
                            alt={project.title}
                            className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700 ease-out"
                            loading="lazy"
                          />
                          {/* Hover overlay */}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end justify-center pb-6">
                            <span className="px-5 py-2.5 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm text-primary dark:text-teal font-semibold rounded-xl shadow-xl flex items-center gap-2 text-sm transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                              View Project
                              <FaArrowRight className="text-xs" />
                            </span>
                          </div>
                          {/* Category badge */}
                          <div className="absolute top-3 left-3">
                            <span
                              className={`inline-flex px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg bg-gradient-to-r ${color} text-white shadow-md`}
                            >
                              {project.category}
                            </span>
                          </div>
                          {/* Year badge */}
                          <div className="absolute top-3 right-3">
                            <span className="px-2 py-1 text-[10px] font-bold bg-black/50 backdrop-blur-sm text-white rounded-lg">
                              {project.year}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Card Content */}
                      <div className="p-5">
                        <h3 className="text-base lg:text-lg font-bold text-gray-900 dark:text-white mb-1.5 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-teal dark:group-hover:from-teal dark:group-hover:to-primary transition-all duration-300 truncate">
                          {project.title}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed line-clamp-2 mb-3">
                          {project.description}
                        </p>
                        {/* Tech pills */}
                        <div className="flex flex-wrap gap-1.5">
                          {(project.techStack || []).slice(0, 3).map((tech) => (
                            <span
                              key={tech}
                              className="px-2 py-0.5 text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md"
                            >
                              {tech}
                            </span>
                          ))}
                          {(project.techStack || []).length > 3 && (
                            <span className="px-2 py-0.5 text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 rounded-md">
                              +{project.techStack.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  </SwiperSlide>
                );
              })}
            </Swiper>

            {/* Custom navigation buttons */}
            <div className="flex justify-center gap-3 mt-2">
              <button
                className="ourwork-swiper-prev p-3 bg-white dark:bg-gray-800 rounded-full shadow-md border border-gray-200 dark:border-gray-700 hover:border-primary dark:hover:border-teal hover:shadow-lg transition-all duration-300 group"
                aria-label="Previous slide"
              >
                <FaChevronLeft className="text-gray-400 group-hover:text-primary dark:group-hover:text-teal transition-colors duration-300" />
              </button>
              <button
                className="ourwork-swiper-next p-3 bg-white dark:bg-gray-800 rounded-full shadow-md border border-gray-200 dark:border-gray-700 hover:border-primary dark:hover:border-teal hover:shadow-lg transition-all duration-300 group"
                aria-label="Next slide"
              >
                <FaChevronRight className="text-gray-400 group-hover:text-primary dark:group-hover:text-teal transition-colors duration-300" />
              </button>
            </div>
          </div>

          {/* Mobile: single-slide swiper */}
          <div className="block md:hidden">
            <Swiper
              modules={[Pagination, Autoplay]}
              slidesPerView={1.1}
              spaceBetween={16}
              centeredSlides
              pagination={{ clickable: true, dynamicBullets: true }}
              autoplay={{
                delay: 4000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              className="portfolio-swiper pb-12"
            >
              {featuredProjects.map((project) => {
                const imageSrc = getProjectImage(project.image);
                const color = getColor(project.category);

                return (
                  <SwiperSlide key={project.id}>
                    <button
                      onClick={() => openModal(project)}
                      className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-700 w-full text-left focus:outline-none"
                      aria-label={`View ${project.title} details`}
                    >
                      {imageSrc && (
                        <div className="relative overflow-hidden aspect-[16/10]">
                          <img
                            src={imageSrc}
                            alt={project.title}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                          <div className="absolute top-3 left-3">
                            <span
                              className={`inline-flex px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg bg-gradient-to-r ${color} text-white shadow-md`}
                            >
                              {project.category}
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="p-5">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1.5 truncate">
                          {project.title}
                        </h3>
                        <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed line-clamp-2 mb-3">
                          {project.description}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {(project.techStack || []).slice(0, 3).map((tech) => (
                            <span
                              key={tech}
                              className="px-2 py-0.5 text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                    </button>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>

        {/* View All Projects CTA */}
        <div className="text-center animate-fade-in-up">
          <button
            onClick={() => {
              navigate("/our-work");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="group relative inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-primary to-teal hover:from-primary-600 hover:to-teal-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
          >
            <span className="relative z-10 flex items-center gap-2">
              View All {portfolioData.projects.length} Projects
              <FaArrowRight className="group-hover:translate-x-1 transition-transform duration-300" />
            </span>
            <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {modalProject && (
        <ProjectModal
          project={modalProject}
          onClose={closeModal}
          onPrev={() => navigateModal(-1)}
          onNext={() => navigateModal(1)}
          hasPrev={modalIndex > 0}
          hasNext={modalIndex < featuredProjects.length - 1}
        />
      )}
    </section>
  );
};

export default OurWork;
